import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banknote, CreditCard, Landmark, PiggyBank, Plus, Trash2, TrendingUp, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { cabecalhoEmpresa, del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { dinheiro, agenciaBancaria, numeroConta } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useContas } from './dados';
import { ExtratoConta } from './ExtratoConta';
import { invalidarFinanceiro, TIPOS_CONTA, type Conta, type TipoConta } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

const ICONE = { banco: Landmark, caixa: Banknote, cartao: CreditCard, investimento: TrendingUp, poupanca: PiggyBank } as const;
/** Fase 2, W24 (K9, B1): conta com agência e número (banco, investimento e poupança); caixa não tem banco, cartão só o emissor. */
const COM_AGENCIA = new Set<unknown>(['banco', 'investimento', 'poupanca'] satisfies TipoConta[]);

/** M4.1: cards das contas e caixas com o saldo de hoje; criar, editar, inativar e remover (sem uso). */
export function Contas() {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useContas();
  const [aberto, setAberto] = useState<Conta | 'nova' | null>(null);
  const [remover, setRemover] = useState<Conta | null>(null);
  const [extrato, setExtrato] = useState<Conta | null>(null);
  const [importar, setImportar] = useState(false);
  const atual = aberto && aberto !== 'nova' ? aberto : null;

  const excluir = useMutation({
    mutationFn: (c: Conta) => del(`/financeiro/contas/${c.id}`),
    onSuccess: async () => {
      await invalidarFinanceiro(qc);
      setRemover(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const campos: DefCampo[] = [
    // V2 verticais: conta nova sem empresa na aba (sem filtro, várias permitidas) pede a empresa.
    ...(atual ? [] : campoEmpresaDoRegistro(s.empresas, { rotulo: tn('empresa.rotulo'), ajuda: tn('empresa.ajudaRegistro') })),
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: TIPOS_CONTA.map((x) => ({ valor: x, rotulo: t(`tipoConta.${x}`) })) },
    { nome: 'banco', rotulo: t('campos.banco'), tipo: 'texto', largura: 'terco', visivel: (v) => v.tipo !== 'caixa' },
    { nome: 'agencia', rotulo: t('campos.agencia'), tipo: 'texto', largura: 'terco', visivel: (v) => COM_AGENCIA.has(v.tipo) },
    { nome: 'conta', rotulo: t('campos.numeroConta'), tipo: 'texto', largura: 'terco', visivel: (v) => COM_AGENCIA.has(v.tipo) },
    { nome: 'saldo_inicial_centavos', rotulo: t('campos.saldoInicial'), tipo: 'dinheiro', largura: 'meia', ajuda: t('ajuda.saldoInicial') },
    { nome: 'moeda', rotulo: t('campos.moeda'), tipo: 'texto', largura: 'meia', desabilitado: !!atual, ajuda: atual ? t('ajuda.moedaTravada') : undefined },
    { nome: 'padrao', rotulo: t('campos.padrao'), tipo: 'booleano', largura: 'meia', ajuda: t('ajuda.padrao') },
    { nome: 'ativa', rotulo: t('campos.ativa'), tipo: 'booleano', largura: 'meia' },
  ];

  return (
    <>
      {s.pode('financeiro.criar') && (
        <AcaoPrimaria>
          {s.pode('financeiro.excluir') && <BotaoLixeira entidades={['financeiro.conta']} />}
          {/* Fase 2 (W18, K3): importador de contas e caixas (framework do núcleo, atualiza pelo nome). */}
          {/* Val5 (29/10): importar pede `financeiro.importar`. */}
          {s.pode('financeiro.importar') && s.pode('financeiro.criar') && s.pode('financeiro.editar') && (
            <>
              <BotaoModeloCsv caminho="/financeiro/contas" />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('acoes.importarContas')}
              </Botao>
            </>
          )}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
            {t('acoes.novaConta')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
        {(d) => (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {d.itens.map((c) => {
              const Icone = ICONE[c.tipo] ?? Landmark;
              return (
                <li key={c.id} className="min-w-0">
                  <Cartao
                    className={c.ativa ? '' : 'opacity-70'}
                    titulo={
                      <span className="flex min-w-0 items-center gap-2">
                        <Icone className="size-4 shrink-0 text-acento-forte" aria-hidden />
                        <span className="truncate" title={c.nome}>{c.nome}</span>
                      </span>
                    }
                    acao={
                      <span className="flex flex-wrap justify-end gap-1">
                        {c.padrao && <Selo cor="acento">{t('estado.padrao')}</Selo>}
                        {!c.ativa && <Selo>{t('estado.inativa')}</Selo>}
                      </span>
                    }
                  >
                    <p className="kicker">{t('contas.saldoHoje')}</p>
                    <TextoCortado como="p" className={`numero mt-1 text-2xl font-semibold ${c.saldo_centavos < 0 ? 'text-perigo' : 'text-tinta-1'}`}>{dinheiro(c.saldo_centavos, c.moeda)}</TextoCortado>
                    {(() => {
                      const descricao = [t(`tipoConta.${c.tipo}`), c.banco, c.agencia && `${t('campos.agencia')} ${agenciaBancaria(c.agencia)}`, c.conta && `${t('campos.numeroConta')} ${numeroConta(c.conta)}`].filter(Boolean).join(', ');
                      return (
                        <p className="mt-2 min-h-5 truncate text-xs text-tinta-3" title={descricao}>
                          {descricao}
                        </p>
                      );
                    })()}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link to={`/financeiro/financeiro/entradas-saidas?conta_id=${c.id}`} className="inline-flex h-8 items-center rounded-lg border border-borda px-3 text-sm font-medium text-tinta-2 hover:bg-sup-2 hover:text-tinta-1">
                        {t('contas.verLancamentos')}
                      </Link>
                      <Botao tamanho="sm" onClick={() => setExtrato(c)}>
                        {t('extrato.abrir')}
                      </Botao>
                      {s.pode('financeiro.editar') && (
                        <Botao tamanho="sm" onClick={() => setAberto(c)}>
                          {tn('comum.editar')}
                        </Botao>
                      )}
                      {s.pode('financeiro.excluir') && (
                        <Botao tamanho="sm" variante="fantasma" aria-label={tn('comum.excluir')} icone={<Trash2 className="size-4" />} onClick={() => setRemover(c)} />
                      )}
                    </div>
                  </Cartao>
                </li>
              );
            })}
          </ul>
        )}
      </Estado>
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? atual.nome : t('acoes.novaConta')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-conta">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'nova'}
          id="form-conta"
          campos={campos}
          entidade="financeiro.conta"
          inicial={atual ? { ...atual } : { tipo: 'banco', moeda: 'BRL', saldo_inicial_centavos: 0, ativa: true, padrao: false }}
          aoEnviar={async (v) => {
            const corpo = {
              nome: v.nome,
              tipo: v.tipo,
              banco: v.tipo === 'caixa' ? null : v.banco || null,
              agencia: COM_AGENCIA.has(v.tipo) ? v.agencia || null : null,
              conta: COM_AGENCIA.has(v.tipo) ? v.conta || null : null,
              saldo_inicial_centavos: v.saldo_inicial_centavos ?? 0,
              moeda: String(v.moeda || 'BRL').toUpperCase(),
              ativa: !!v.ativa,
              padrao: !!v.padrao,
            };
            if (atual) await patch(`/financeiro/contas/${atual.id}`, corpo);
            else await post('/financeiro/contas', corpo, cabecalhoEmpresa(empresaEscolhida(v)));
            await invalidarFinanceiro(qc);
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Drawer>
      <ExtratoConta conta={extrato} aoFechar={() => setExtrato(null)} />
      <Confirmar
        aberto={!!remover}
        aoFechar={() => setRemover(null)}
        aoConfirmar={() => remover && excluir.mutate(remover)}
        carregando={excluir.isPending}
        perigoso
        titulo={t('contas.removerTitulo')}
        texto={t('contas.removerTexto')}
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho="/financeiro/contas" titulo={t('acoes.importarContas')} aoConcluir={() => void invalidarFinanceiro(qc)} />
    </>
  );
}
